import React, { useContext } from 'react';
import Data from '../../data';
import Modal from '../../modal';
import UI from '../../ui';
import Utils from '../../utils';
import { Response } from '../../utils/CommunicationTypes';
import AddCarrierHeader from '../AddCarrierHeader.component';

const { Carrier } = Data;
const { Section, SectionBody, Footer, WrappedCheckbox, Link, Fineprint } = UI;
const { Session, Communication } = Utils;
const { ModalContext, StatusModal } = Modal;

type AddFedExWalletAccountProps = {
	readonly completionCallback: (
		success: boolean,
		carrier?: string,
		locationsLength?: number,
		carrierType?: string
	) => void;
	readonly backAction?: () => void;
	readonly custId?: string;
	readonly showSuccessModal?: boolean;
};

export function AddFedExWalletAccount({
	completionCallback,
	backAction = null,
	custId = null,
	showSuccessModal = false,
}: AddFedExWalletAccountProps) {
	const [termsAccepted, setTermsAccepted] = React.useState<boolean>(false);
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const { showError, showModal } = useContext(ModalContext);

	const onSubmit = async () => {
		setIsLoading(true);
		const { customerDetails = {} } = Session.getAccountInfo();
		const address = customerDetails.address || {};

		const params = {
			Carrier: 'FedEx',
			UserOwnedAccount: false,
			Address: {
				FullName: address.name,
				Company: address.company,
				Address1: address.addressLine1,
				Address2: address.addressLine2,
				City: address.city,
				State: address.stateProvince,
				ZIPCode: address.postalCode.slice(0, 5),
				ZIPCodeAddOn: address.postalCode.slice(6, 10),
				PhoneNumber: address.phone,
				EmailAddress: customerDetails.email,
			},
			AgreeToEula: true,
		};

		let response: Response;

		if (custId) {
			response = await Communication.addCarrierToLocation(params);
		} else {
			response = await Communication.addCarrier(params);
		}

		setIsLoading(false);

		if (Communication.isSuccessful(response)) {
			if (showSuccessModal) {
				showModal({
					component: ({ modalId }) => (
						<StatusModal
							modalId={modalId}
							header="Connection Request Submitted"
							title="Your FedEx request is submitted and under review."
							description="Once approved, typically within 24 hours, you'll be able to access FedEx services."
							dataqa="fedex-connection-success-modal"
							iconType="success"
							onClose={() =>
								completionCallback(
									true,
									Carrier.FedEx,
									undefined,
									'FedExReseller'
								)
							}
						/>
					),
				});
			} else {
				completionCallback(true, Carrier.FedEx, undefined, 'FedExReseller');
			}
		} else {
			showError({
				data: {
					title: 'Connection Error',
					description:
						'<p><b>Your FedEx account could not be connected.</b></p><p>Check the account details you entered and try again. If you keep receiving this error, call support at <b>1-888-434-0055</b> (available Monday - Friday from 8am - 8pm CT).</p>',
					primaryButtonText: 'OK',
					handleType: 'custom',
				},
			});
		}
	};

	const submitButtonLabel = isLoading ? 'Submitting...' : 'Submit Request';

	return (
		<Section>
			<AddCarrierHeader
				title="FedEx Terms and Conditions"
				carrier={Carrier.FedEx}
				subtitle="To ship with FedEx, please review and accept the updated terms of service. Your request will then be reviewed and approved, usually within 24 hours."
			/>
			<SectionBody>
				<div className="fedex-terms-body">
					<Link
						href={`https://content.stamps.com/ContentService/conditions/fedex-merchant-agreement-for-stamps.html`}
						target="_blank"
						dataqa="fedex-terms-link"
						navIcon={true}
					>
						FedEx Terms of Service
					</Link>
					<WrappedCheckbox
						name="fedex-terms-checkbox"
						checked={termsAccepted}
						onChange={() => {
							setTermsAccepted(!termsAccepted);
						}}
						inputId="fedex-terms-checkbox"
						htmlFor="fedex-terms-checkbox"
						dataqa="fedex-terms-checkbox"
					>
						I have read and agree to the FedEx Terms and Conditions.
					</WrappedCheckbox>
					<Fineprint>
						FedEx service marks are owned by Federal Express Corporation and are
						used by permission.
					</Fineprint>
				</div>
			</SectionBody>
			<Footer
				primary={{
					label: submitButtonLabel,
					onClick: onSubmit,
					dataqa: 'fedex-submit-request-btn',
					disabled: !termsAccepted || isLoading,
					loading: isLoading,
				}}
				secondary={{
					label: 'Cancel',
					onClick: () => completionCallback(false),
					dataqa: 'fedex-cancel-btn',
				}}
				backAction={backAction}
			></Footer>
		</Section>
	);
}
